import { useState, useEffect, useRef, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useStoreState, useStoreActions } from '~/store';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useIntl, FormattedMessage, FormattedDate } from 'react-intl';
import Helmet from 'Components/Helmet';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import Icon from '@/components/Icon';
import classNames from 'classnames';
import { useActiveListings } from './hooks/useActiveListings';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const LinkWithSolutionSwitch = ({
  to,
  solutionId,
  children,
  ...props
}: {
  to: string;
  solutionId?: string;
  children: ReactNode;
  className?: string;
}) => {
  const currentSolutionId = useStoreState((s) => s.auth.solutionId);
  const changeSolution = useStoreActions((a) => a.auth.changeSolution);

  const handleClick = () => {
    if (solutionId && solutionId !== currentSolutionId) {
      changeSolution(solutionId);
    }
  };

  return (
    <Link to={to} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
};

const TIMING_MESSAGES = {
  'Ends in': (duration: string) => (
    <FormattedMessage
      id="countdown-ends-in"
      defaultMessage="Ends in {duration}"
      values={{ duration }}
    />
  ),
  'Starts in': (duration: string) => (
    <FormattedMessage
      id="countdown-starts-in"
      defaultMessage="Starts in {duration}"
      values={{ duration }}
    />
  ),
  'Expires in': (duration: string) => (
    <FormattedMessage
      id="countdown-expires-in"
      defaultMessage="Expires in {duration}"
      values={{ duration }}
    />
  ),
};

const FLAG_MESSAGES: Record<string, ReactNode> = {
  qa_action_required: (
    <FormattedMessage id="home-flag-qa" defaultMessage="Q&A action required" />
  ),
  counter_action_required: (
    <FormattedMessage
      id="home-flag-counter"
      defaultMessage="Counter action required"
    />
  ),
};

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const WARNING_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
const CRITICAL_HOURS_MS = 4 * 60 * 60 * 1000;

const Countdown = ({
  targetDate,
  prefix,
  isEnded: serverEnded,
}: {
  targetDate: string;
  prefix: string;
  isEnded?: boolean;
}) => {
  const [, setTick] = useState(0);
  const diff = dayjs(targetDate).diff(dayjs());
  const hasEnded = serverEnded || diff <= 0;

  useEffect(() => {
    if (hasEnded) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [hasEnded]);

  const isEnding = prefix === 'Ends in' || prefix === 'Expires in';
  const isStarting = prefix === 'Starts in';
  const isCritical = isEnding && !hasEnded && diff <= CRITICAL_HOURS_MS;
  const isWarning =
    (isEnding && !hasEnded && diff <= WARNING_DAYS_MS) ||
    (isStarting && !hasEnded && diff <= CRITICAL_HOURS_MS);

  const content = hasEnded ? (
    <FormattedMessage id="countdown-ended" defaultMessage="Ended" />
  ) : diff >= ONE_WEEK_MS ? (
    <FormattedDate
      value={new Date(targetDate)}
      year="numeric"
      month="short"
      day="numeric"
    />
  ) : (
    (TIMING_MESSAGES[prefix as keyof typeof TIMING_MESSAGES]?.(
      dayjs.duration(diff).humanize()
    ) ?? dayjs.duration(diff).humanize())
  );

  return (
    <span
      className={classNames(
        'tw:inline-flex tw:gap-1 tw:items-center tw:text-xs tw:whitespace-nowrap',
        {
          'tw:font-bold': isWarning,
          'tw:text-red-600': isCritical,
        }
      )}
    >
      <Icon name="schedule" size="small" />
      {content}
    </span>
  );
};

const MESSAGES = {
  type: {
    Tender: <FormattedMessage id="home-type-tender" defaultMessage="Tender" />,
    Offer: <FormattedMessage id="home-type-offer" defaultMessage="Offer" />,
    Bid: <FormattedMessage id="home-type-bid" defaultMessage="Bid" />,
    MyOffer: (
      <FormattedMessage id="home-type-my-offer" defaultMessage="My offer" />
    ),
    MyBid: <FormattedMessage id="home-type-my-bid" defaultMessage="My bid" />,
  },
  counter: (
    <FormattedMessage id="home-badge-counter" defaultMessage="Countered" />
  ),
  pending: (
    <FormattedMessage id="home-badge-pending" defaultMessage="Pending" />
  ),
  scheduled: (
    <FormattedMessage id="home-badge-scheduled" defaultMessage="Scheduled" />
  ),
  scheduledTooltip: (date: ReactNode) => (
    <FormattedMessage
      id="home-badge-scheduled-tooltip"
      defaultMessage="Scheduled for {date}"
      values={{ date }}
    />
  ),
};

export default function Home() {
  const { formatMessage } = useIntl();
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useActiveListings();

  const listings = data?.pages.flatMap((page) => page.listings) ?? [];
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [hoveredFlagId, setHoveredFlagId] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<number>();

  useEffect(() => {
    return () => clearTimeout(hoverTimeoutRef.current);
  }, []);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      },
      { rootMargin: '100px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetching, fetchNextPage]);

  return (
    <div className="tw tw:px-4 tw:lg:px-6 tw:space-y-2 tw:mb-2">
      <Helmet titleTemplate="%s" defaultTitle="Nui Markets">
        <title>
          {`Nui Markets - ${formatMessage({ id: 'home-page-title', defaultMessage: 'Home' })}`}
        </title>
        <link rel="shortcut icon" href="/favicon.ico" />
      </Helmet>

      {isLoading && (
        <div className="tw:space-y-2">
          {[...Array(20)].map((_, i) => (
            <Card key={i} className="tw:p-0">
              <CardContent className="tw:md:flex tw:items-center tw:gap-4 tw:px-4 tw:py-3 tw:min-h-16 tw:animate-pulse">
                <div className="tw:flex tw:items-center tw:gap-3 tw:flex-shrink-0 tw:md:w-[270px]">
                  <div className="tw:w-6 tw:h-6 tw:rounded-full tw:bg-gray-200" />
                  <div className="tw:h-4 tw:w-32 tw:bg-gray-200 tw:rounded" />
                </div>
                <div className="tw:h-4 tw:w-16 tw:bg-gray-200 tw:rounded" />
                <div className="tw:flex-1 tw:h-4 tw:bg-gray-200 tw:rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && !error && listings.length === 0 && (
        <Card className="tw:p-4 tw:text-gray-500">
          <FormattedMessage
            id="home-no-results"
            defaultMessage="No listings found"
          />
        </Card>
      )}

      {error && listings.length === 0 && (
        <Card className="tw:p-4 tw:text-red-600">
          <FormattedMessage
            id="home-error-loading"
            defaultMessage="Error loading listings"
          />
        </Card>
      )}

      {listings.map((listing) => (
        <LinkWithSolutionSwitch
          key={listing.id}
          to={listing.href}
          solutionId={listing.solutionId}
          className="tw:block tw:no-underline"
        >
          <Card
            className={classNames(
              `tw:p-0 tw:hover:shadow-md tw:transition-shadow tw:cursor-pointer tw:text-sm`,
              {
                'tw:border tw:border-tender': listing.type === 'Tender',
              }
            )}
            onMouseEnter={() => {
              if (listing.icons?.isFlagged) {
                hoverTimeoutRef.current = window.setTimeout(
                  () => setHoveredFlagId(listing.id),
                  250
                );
              }
            }}
            onMouseLeave={() => {
              clearTimeout(hoverTimeoutRef.current);
              setHoveredFlagId(null);
            }}
          >
            <CardContent className="tw:md:flex tw:items-center tw:gap-4 tw:px-4 tw:py-3 tw:min-h-16 tw:relative">
              <div className="tw:flex tw:items-center tw:gap-3 tw:flex-shrink-0 tw:md:w-[270px] tw:mb-1 tw:md:mb-0">
                {listing.company?.icon ? (
                  <img
                    src={listing.company.icon}
                    alt=""
                    className="tw:w-6 tw:h-6 tw:rounded-full tw:object-cover"
                  />
                ) : (
                  <div className="tw:w-6 tw:h-6 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:bg-gray-200 tw:text-gray-700 tw:text-sm">
                    {listing.company?.name?.charAt(0)}
                  </div>
                )}
                <div className="tw:text-gray-900 tw:whitespace-nowrap">
                  {listing.company?.name}
                </div>
              </div>

              <div className="tw:absolute tw:md:static tw:top-3 tw:right-3 tw:md:w-[75px]">
                {listing.type === 'Tender' && (
                  <Badge variant="tender" className="tw:w-full">
                    {MESSAGES.type.Tender}
                  </Badge>
                )}
                {listing.type === 'Offer' && (
                  <Badge variant="offer" className="tw:w-full">
                    {listing.isOwner
                      ? MESSAGES.type.MyOffer
                      : MESSAGES.type.Offer}
                  </Badge>
                )}
                {listing.type === 'Bid' && (
                  <Badge variant="bid" className="tw:w-full">
                    {listing.isOwner ? MESSAGES.type.MyBid : MESSAGES.type.Bid}
                  </Badge>
                )}
              </div>

              <div className="tw:flex-1 tw:min-w-0 tw:flex tw:gap-3 tw:items-center">
                <div>
                  <div>{listing.title}</div>
                  {listing.attributes &&
                    (() => {
                      const attributesToRender = Object.entries(
                        listing.attributes
                      )
                        .filter(([, values]) => Boolean(values?.[0]?.name))
                        .sort(([a], [b]) => a.localeCompare(b));

                      if (attributesToRender.length === 0) return null;

                      return (
                        <div className="tw:text-xs">
                          {attributesToRender.map(([key, values], i, arr) => (
                            <span key={key}>
                              <span className="tw:capitalize">{key}</span>{' '}
                              <span className="tw:text-gray-500">
                                {values[0].name}
                              </span>
                              {i < arr.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  {(listing.subtitle || listing.isLive) && (
                    <div className="tw:text-xs tw:text-gray-500 tw:mt-1">
                      {listing.subtitle && (
                        <span className="tw:mr-2">{listing.subtitle}</span>
                      )}
                      {listing.isLive && (
                        <>
                          <span className="tw:relative tw:inline-flex tw:h-2 tw:w-2 tw:mr-1">
                            <span className="tw:animate-ping tw:absolute tw:inline-flex tw:h-full tw:w-full tw:rounded-full tw:bg-green-400 tw:opacity-75" />
                            <span className="tw:relative tw:inline-flex tw:rounded-full tw:h-2 tw:w-2 tw:bg-green-500" />
                          </span>
                          <FormattedMessage
                            id="home-live"
                            defaultMessage="Live"
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="tw:inline-flex tw:gap-2">
                  {listing.badges?.map((badge, index) => {
                    if (badge.variant === 'counter') {
                      return (
                        <Badge key={index} variant="counter">
                          {MESSAGES.counter}
                        </Badge>
                      );
                    }
                    if (badge.variant === 'pending') {
                      return (
                        <Badge key={index} variant="pending">
                          {MESSAGES.pending}
                        </Badge>
                      );
                    }
                    if (badge.variant === 'scheduled') {
                      if (!badge.scheduledtime) {
                        return (
                          <Badge key={index} variant="scheduled">
                            {MESSAGES.scheduled}
                          </Badge>
                        );
                      }
                      return (
                        <Tooltip key={index}>
                          <TooltipTrigger asChild>
                            <span>
                              <Badge variant="scheduled">
                                {MESSAGES.scheduled}
                              </Badge>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {MESSAGES.scheduledTooltip(
                              <FormattedDate
                                value={badge.scheduledtime}
                                year="numeric"
                                month="short"
                                day="numeric"
                                hour="numeric"
                                minute="numeric"
                              />
                            )}
                          </TooltipContent>
                        </Tooltip>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>

              <div className="tw:flex tw:items-center tw:gap-2 tw:flex-shrink-0 tw:mt-1 tw:md:mt-0">
                {listing.icons?.isLocked && (
                  <Icon name="lock" size="small" className="tw:text-gray-500" />
                )}
                {listing.icons?.isFlagged && (
                  <Tooltip open={hoveredFlagId === listing.id}>
                    <TooltipTrigger asChild>
                      <span className="tw:w-6 tw:h-6 tw:inline-flex tw:justify-center tw:items-center">
                        <Icon
                          name="flag"
                          size="small"
                          className="tw:text-red-500"
                          style={{ fontVariationSettings: "'FILL' 1" }}
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {(() => {
                        const validFlags = (
                          listing.icons.activeFlags || []
                        ).filter((f) => FLAG_MESSAGES[f]);
                        if (validFlags.length === 0) return null;
                        return validFlags.length === 1 ? (
                          FLAG_MESSAGES[validFlags[0]]
                        ) : (
                          <ul className="tw tw:list-disc tw:pl-4 tw:m-0">
                            {validFlags.map((flag) => (
                              <li key={flag}>{FLAG_MESSAGES[flag]}</li>
                            ))}
                          </ul>
                        );
                      })()}
                    </TooltipContent>
                  </Tooltip>
                )}

                {listing.status !== 'Pending' && (
                  <Countdown
                    targetDate={listing.timing.targetDate}
                    prefix={listing.timing.prefix}
                    isEnded={listing.timing.isEnded}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </LinkWithSolutionSwitch>
      ))}

      <div ref={loadMoreRef} />
      {isFetchingNextPage && (
        <div className="tw:flex tw:justify-center tw:py-4">
          <div className="tw:animate-spin tw:rounded-full tw:h-6 tw:w-6 tw:border-2 tw:border-gray-300 tw:border-t-gray-600" />
        </div>
      )}
    </div>
  );
}
